import shortid from "shortid";
import ModificationType from './reference/ModificationType'
import RecognitionRule from './reference/RecognitionRule';
import ExpenseRecognitionRule from './reference/ExpenseRecognitionRule'
import * as AccountingUtils from './AccountingUtils';
import { Utils, Comparator, moment, Period } from 'revlock-webutils';
import { getStandAlonePrice } from './StandalonePriceHelper';
import FixedMonthRatablePlan from "./plan/FixedMonthRatablePlan";
import FullMonthRatablePlan from "./plan/FullMonthRatablePlan";

import { updatePSPlan } from "./ProfessionalServiceHelper"
import { Codes } from '../../build/core/ErrorCode';
import { selectSSP } from './SSPSelectorHelper';
import { selectExpenseRule } from './ExpenseRuleSelectorHelper';

import Big from "big.js";
import PointInTimePlan from "./plan/PointInTimePlan";
import DailyRatablePlan from './plan/DailyRatablePlan';
import ApportionedRatablePlan from './plan/ApportionedRatablePlan'

import { updateRatablePlan } from "./plan/ApportionedRatablePlan"

import Modified30_360Plan from './plan/Modified30_360Plan'
import _ from "lodash";

const { formatDate, getAbsoluteMonths } = Utils;

const defaultSSPForProductCode = (id, attributes) => ({
    attributes: attributes || {},
    commissionRecognitionRuleId: 2,
    deliveryStartDate: "",
    recognitionRuleId: RecognitionRule.POINT_IN_TIME,
    id,
    standalonePrice: 0
})

const defaultDirectExpenseRule = (id, expenseAttributes, expenseCode) => {
    return {
        "sspType": "Direct",
        "expenseAttributes": expenseAttributes,
        "expenseCode": expenseCode,
        "reAllocateExpense": true,
        "expenseTracksRevenue": true,
        "id": id
    }
}

const defaultInDirectExpenseRule = (id, expenseItem) => {
    const expenseCode = expenseItem.expenseCode

    let expenseAmortizationMethod = ExpenseRecognitionRule.AS_INCURRED

    if (expenseItem.amortizationTerm != undefined ||
        (expenseItem.amortizationEndDate != undefined && (
            Utils.isDate(expenseItem.amortizationEndDate)
            && expenseItem.amortizationEndDate != expenseItem.amortizationStartDate
        ))
    ) {
        expenseAmortizationMethod = ExpenseRecognitionRule.OVER_TIME
    }

    return {
        "sspType": "Indirect",
        "expenseAttributes": {},
        "expenseCode": expenseCode,
        "expenseAmortizationMethod": expenseAmortizationMethod,
        "id": id
    }
}

export function computeTermListPrice(product, startDate, endDate) {
    const listPriceAmount = product.listPriceAmount || product.listPrice
    return computeTermAmount(product, startDate, endDate, listPriceAmount)
}

export function computeTermAmount(product, startDate, endDate, amount) {
    if (!product)
        return null;

    const { recurring } = product;

    if (!recurring || !product.listPricePeriodUnit)
        return amount

    const { listPricePeriodUnit, listPricePeriod } = product;

    if (!amount)
        return null;

    const daysPerYear = 365
    const daysPerMonth = new Big(365).div(12)
    const daysPerQuarter = new Big(365).div(4)

    let dayPrice;

    switch (listPricePeriodUnit) {
        case 'years':
            dayPrice = new Big(amount).div(listPricePeriod).div(daysPerYear)
            break;
        case 'months':
            dayPrice = new Big(amount).div(listPricePeriod).div(daysPerMonth)
            break;
        case 'quarters':
            dayPrice = new Big(amount).div(listPricePeriod).div(daysPerQuarter)
            break;
    }

    function termInDays() {

        if (startDate && endDate) {
            const start = moment(startDate, "YYYY-MM-DD");
            const end = moment(endDate, "YYYY-MM-DD");

            if (start.date() == end.date() && start.month() == end.month()) {
                return end.diff(start, 'years') * 365;
            }
            //if you are on the same day/month combo then go ahead and just compute flatly against year

            return end.diff(start, 'days');
            //otherwise compute exact days
        }

        if (product.term) {
            return new Big(product.term).times(daysPerMonth);
        }
        //convert term in months as specified into days

        return daysPerMonth;
        //assume that the term is 1 month if not specified for a recurring product
    }

    const term = termInDays();

    const listPrice =
        dayPrice.times(term).toFixed(4);
    return Number(listPrice);
}

export function populateClientAttributes(clientAttributes, entities, params) {

    clientAttributes && entities && entities.forEach(entity => {

        clientAttributes = clientAttributes.filter(attribute => attribute.entity == entity);

        let attributes = {}

        clientAttributes.forEach(attribute => {
            attributes[attribute.name] = AccountingUtils.evaluateExpression(attribute.expression, params);
        })

        params[entity].attributes = Object.assign({}, params[entity].attributes, attributes);
    })
}

const populateAnnualExtendedSalePrice = (salesOrderItem) => {
    const { startDate, endDate } = salesOrderItem;

    let duration = 12;

    if (startDate && endDate) {
        duration = Utils.getMonthDiff(startDate, endDate, true);
    }

    if (duration == 0) {
        duration = 12
    }

    salesOrderItem.annualExtendedSalePrice = salesOrderItem.extendedSalePrice * (12 / duration);
    salesOrderItem.duration = duration;
}

const getSOI = (salesOrder, itemId) => {
    const soi = salesOrder.salesOrderItem.find(item => {
        const _itemId = item.rootId || item.id
        if (_itemId === itemId) {
            return item
        }
    })

    return soi
}

export const getRevenueArrangementItemBySalesOrderItem = (revenueArrangement, salesOrderItem) => {
    return revenueArrangement && revenueArrangement.revenueArrangementItem.find(rai => {
        let itemId = rai.salesOrderItem.rootId || rai.salesOrderItem.id
        const _itemId = salesOrderItem.rootId || salesOrderItem.id
        if (itemId === _itemId) {
            return rai
        }
    })
}

export function populateExpenseDeliveryDates(expenseItem, expenseRule, rai, errors, endDateInclusive, contractTermEnabled, raiItemsGroupedByProductCode) {

    const overrideDeliveryDates = (amortizationStartDate, amortizationEndDate, term, expenseItem, sourceRuleId, targetRuleId) => {

        let deliveryStartDate
        let deliveryEndDate
        if (expenseItem.amortizationStartDate != undefined) {
            if (expenseItem.amortizationStartDate == 'Expense Date') {
                amortizationStartDate = expenseItem.expenseDate
            } else {
                amortizationStartDate = expenseItem.amortizationStartDate
            }
        }

        if (expenseItem.amortizationEndDate != undefined) {
            if (expenseItem.amortizationEndDate == 'Expense Date') {
                amortizationEndDate = expenseItem.expenseDate
            } else {
                amortizationEndDate = expenseItem.amortizationEndDate
            }
        }

        if (expenseItem.amortizationTerm != undefined) {
            amortizationEndDate = undefined
            term = expenseItem.amortizationTerm
        }

        deliveryStartDate = amortizationStartDate

        // STEP 3: We have found the dates at this point, let's populate them now.
        if (sourceRuleId != targetRuleId) {
            // RATABLE
            if (term != undefined && amortizationEndDate == undefined) {
                let endDate = Utils.addMonths(amortizationStartDate, term)

                if (endDateInclusive)
                    endDate = Utils.addDays(endDate, -1);

                deliveryEndDate = Utils.formatDate(endDate);
            } else {
                deliveryEndDate = amortizationEndDate
            }
        } else {
            deliveryEndDate = amortizationStartDate
        }

        return { deliveryStartDate, deliveryEndDate }
    }

    let amortizationStartDate
    let amortizationEndDate
    let term
    // NOTE: SSP is going to tell us which date we need to use for start and end
    if (expenseRule.expenseTracksRevenue == true) {
        amortizationStartDate = rai.deliveryStartDate
        amortizationEndDate = rai.deliveryEndDate

        // Here we need to pickup the start date end date and term of the sales order item 
        const { deliveryStartDate, deliveryEndDate } = overrideDeliveryDates(
            amortizationStartDate,
            amortizationEndDate,
            term,
            expenseItem,
            rai.ssp.recognitionRuleId,
            RecognitionRule.POINT_IN_TIME
        )

        // final dates to use post dereferecing for plan
        expenseItem.deliveryStartDate = deliveryStartDate
        expenseItem.deliveryEndDate = deliveryEndDate
    } else {
        // expense rule is not tracking revenue, check if we have amortization dates and term defined on
        // expense rule. If not then we have explicit dates / term defined. If yes, dereference them.
        // if no dates found on expense item, it's an error.

        // NOTE: expense rule can only have Expense Date / Amortization Start Date

        // Expense Item dates takes priority over what is defined in the Expense Rule

        // STEP 1. Populate dates based off of expense rule.
        if (expenseRule.amortizationStartDate != undefined) {
            // expense rule is telling us which date to pick for amortization.
            if (expenseRule.amortizationStartDate == 'Expense Date') {
                amortizationStartDate = expenseItem.expenseDate
            } else if (expenseRule.amortizationStartDate == 'Amortization Start Date') {
                amortizationStartDate = expenseItem.amortizationStartDate
            }
        }

        if (expenseRule.amortizationEndDate != undefined) {
            // expense rule is telling us which end date to pick.
            if (expenseRule.amortizationEndDate == 'Expense Date') {
                amortizationEndDate = expenseItem.expenseDate
            } else if (expenseRule.amortizationEndDate == 'Amortization Start Date') {
                amortizationEndDate = expenseItem.amortizationEndDate
            }
        }

        if (expenseRule.amortizationTerm != undefined) {
            // expense rule is telling us what is the term
            amortizationEndDate = undefined
            term = expenseRule.amortizationTerm
        }

        // STEP 2. Override dates based off of what is defined in the expense item.
        const { deliveryStartDate, deliveryEndDate } = overrideDeliveryDates(
            amortizationStartDate,
            amortizationEndDate,
            term,
            expenseItem,
            expenseRule.expenseAmortizationMethod,
            ExpenseRecognitionRule.AS_INCURRED
        )

        expenseItem.deliveryStartDate = deliveryStartDate
        expenseItem.deliveryEndDate = deliveryEndDate
    }

    // no start date item is inactive
    // no end date default end date to start date.

    if (!expenseItem.deliveryStartDate) {
        // this is an error
        expenseItem.deliveryStartDate = expenseItem.expenseDate
    }

    if (!expenseItem.deliveryEndDate) {
        expenseItem.deliveryEndDate = expenseItem.deliveryStartDate
    }


    if (raiItemsGroupedByProductCode && contractTermEnabled && expenseItem.productCode) {
        // we have contract term enabled and we have product code in the expense too
        // we need to pick min start date and max end date from all the sales order items that have same product code
        // as expense.

        if (raiItemsGroupedByProductCode[expenseItem.productCode]) {
            // we found the product
            const raiItems = raiItemsGroupedByProductCode[expenseItem.productCode]
            let minDeliveryStartDate = raiItems[0].deliveryStartDate
            let maxDeliveryEndDate = raiItems[0].deliveryEndDate
            for (const _item of raiItems) {
                if (_item.deliveryStartDate < minDeliveryStartDate) {
                    minDeliveryStartDate = _item.deliveryStartDate
                }

                if (_item.deliveryEndDate > maxDeliveryEndDate) {
                    maxDeliveryEndDate = _item.deliveryEndDate
                }
            }

            expenseItem.deliveryStartDate = minDeliveryStartDate
            expenseItem.deliveryEndDate = maxDeliveryEndDate

        }
    }
}

export function populateDeliveryDates(salesOrder, soi, ssp, rai, errors, customFields, endDateInclusive, dereferenceRuleHasPriority = false) {
    soi.isActive = false

    const lookup = (dateField) => {
        if (dateField.match(/Order Start Date/gi) || dateField.match(/Order Date/gi)) {
            return salesOrder.orderDate;
        }
        //special case for order start date

        let lookupField = dateField;

        const cf =
            customFields && customFields.find((c) => c.displayName === dateField)
        if (cf) {
            lookupField = cf.name;
        }

        if (salesOrder[lookupField]) {
            return salesOrder[lookupField]
        }

        if (salesOrder.orderAttributes) {
            return salesOrder.orderAttributes[lookupField]
        }

        return null;
    }

    const productStartDate = () => {
        if (!soi.product || !soi.product.serviceStartDate || !soi.product.serviceStartDate.trim())
            return null;

        const { serviceStartDate, serviceStartOffset, serviceStartOffsetPeriodUnit }
            = soi.product;

        let startDate =
            moment.isDate(serviceStartDate) ? serviceStartDate : lookup(serviceStartDate);

        if (startDate) {
            startDate = moment(startDate, "YYYY-MM-DD").add(serviceStartOffset, serviceStartOffsetPeriodUnit);
            return Utils.formatDate(startDate);
        }

        return null;
    }

    const searchForDateField = (searchField) => {
        if (!searchField || !searchField.trim()
            || searchField === 'manualDate')
            return null;

        return moment.isDate(searchField) ?
            searchField : lookup(searchField);
    }

    const salesOrderDereferenceStartDate = () => searchForDateField(soi.dereferencedStartDate);
    const sspStartDate = () => searchForDateField(ssp && ssp.deliveryStartDate);

    // In case dereference rules needs to take priority over user selected date,
    // We delete the date and attemp to apply dereference rules.
    let manualStartDate = undefined
    if (dereferenceRuleHasPriority && soi.dereferencedStartDate === 'manualDate') {
        manualStartDate = soi.startDate
        delete soi.startDate
        delete soi.dereferencedStartDate
    }

    // We allow overriding the start date on the UI by specifying a de-referencing field.
    // You can also override by providing it in the SSP or the product
    if (soi.dereferencedStartDate !== 'manualDate') {
        // If dereference source for start date is not salesOrder clear it.
        if (soi.dereferencedStartDateSource && soi.dereferencedStartDateSource !== 'salesOrder') {
            soi.dereferencedStartDate = undefined;
        }

        // Always reset dereference start date value as we will recalcualte it.
        delete soi.dereferencedStartDateValue
        delete soi.startDate

        //0: override at the sales order item level if the user has specified a value
        //to use for a de-reference lookup
        if (soi.dereferencedStartDateSource == 'salesOrder' && soi.dereferencedStartDate) {
            soi.startDate = salesOrderDereferenceStartDate()
        }
        //1. override at the SSP or product level if the user
        //has not specified a date
        else if (ssp && ssp.deliveryStartDate) {
            soi.startDate = sspStartDate();
            soi.dereferencedStartDate = ssp.deliveryStartDate;
            soi.dereferencedStartDateSource = 'ssp';
        } else if (soi.product && soi.product.serviceStartDate) {
            soi.startDate = productStartDate();
            soi.dereferencedStartDate = soi.product.serviceStartDate
            soi.dereferencedStartDateSource = 'product';
        } else {
            soi.dereferencedStartDateValue = undefined
            soi.dereferencedStartDateSource = undefined
        }
    }

    // Unable to apply dereference rules, let's default to the manual date that was set as part of upload
    // or from UI
    if (dereferenceRuleHasPriority && !soi.startDate && manualStartDate) {
        soi.startDate = manualStartDate
        soi.dereferencedStartDate = 'manualDate'
    }

    if (soi.startDate) {
        // We were able to get a good start date
        soi.isActive = true
        soi.dereferencedStartDateValue = soi.startDate
    }

    if (!soi.startDate) {
        //we set a default date here as no date here causes a publishing issuer or something
        //note that the order itself is inactive.
        soi.startDate = salesOrder.orderDate
    }

    const productEndDate = () => {
        if (!soi.product || !soi.product.serviceEndDate || !soi.product.serviceEndDate.trim())
            return null;

        const { serviceEndDate, serviceEndOffset, serviceEndOffsetPeriodUnit }
            = soi.product;

        let endDate =
            moment.isDate(serviceEndDate) ? serviceEndDate : lookup(serviceEndDate);

        if (endDate) {
            if (serviceEndOffset) {
                endDate = moment(endDate, "YYYY-MM-DD").add(serviceEndOffset, serviceEndOffsetPeriodUnit);
            }

            return Utils.formatDate(endDate);
        }

        return null;
    }
    const salesOrderDereferenceEndDate = () => searchForDateField(soi.dereferencedEndDate);
    const sspEndDate = () => searchForDateField(ssp && ssp.deliveryEndDate);

    // In case dereference rules needs to take priority over user selected date,
    // We delete the date and attempt to apply dereference rules.
    let manualEndDate = undefined
    let userSelectedEndDate = undefined
    if (dereferenceRuleHasPriority && soi.dereferencedEndDate === 'manualDate') {
        manualEndDate = soi.endDate
        userSelectedEndDate = soi.userSelectedEndDate
        delete soi.endDate
        delete soi.dereferencedEndDate
        delete soi.userSelectedEndDate
    }

    if (soi.dereferencedEndDate !== 'manualDate') {
        // If dereference source for end date is salesOrder that can't clear it.
        // dont clear for other source, as we will recalculate it.
        if (soi.dereferencedEndDateSource && soi.dereferencedEndDateSource !== 'salesOrder')
            soi.dereferencedEndDate = undefined;

        // Always reset dereference end date value as we will recalcualte it.
        delete soi.dereferencedEndDateValue
        delete soi.endDate

        //0: override at the sales order item level if the user has specified a value
        //to use for a de-reference lookup
        if (soi.dereferencedEndDateSource == 'salesOrder') {
            soi.endDate = salesOrderDereferenceEndDate()
        } else if (ssp && ssp.deliveryEndDate) {
            //1. override at the SSP or product level if the user
            //has not specified a date
            soi.endDate = sspEndDate();
            soi.dereferencedEndDate = ssp.deliveryEndDate;
            soi.dereferencedEndDateSource = 'ssp';
        } else if (soi.product && soi.product.serviceEndDate) {
            soi.endDate = productEndDate();
            soi.dereferencedEndDate = soi.product.serviceEndDate
            soi.dereferencedEndDateSource = 'product';
        } else {
            soi.dereferencedEndDateValue = undefined
            soi.dereferencedEndDateSource = undefined
        }

        if (soi.endDate) {
            soi.dereferencedEndDateValue = soi.endDate

        }
    }

    // derefence end date is manual
    if (soi.startDate && !soi.userSelectedEndDate) {

        if (soi.termSource != 'salesOrder')
            delete soi.term

        let term

        if (soi.term != undefined && soi.termSource == 'salesOrder') {

            // Term in sales order takes priority over dereferenced end date in product and ssp.
            if (!soi.dereferencedEndDateSource || ['product', 'ssp'].includes(soi.dereferencedEndDateSource)) {
                term = soi.term
                soi.termSource = 'salesOrder'
            }

        } else if (ssp.term != undefined && ssp.term) {

            // Term in SSP takes priority over dereferenced end date in product.
            if (!soi.dereferencedEndDateSource || soi.dereferencedEndDateSource == 'product') {
                term = ssp.term
                soi.termSource = 'ssp'
            }
        } else if (soi.product.term != undefined && soi.product.term) {

            if (!soi.dereferencedEndDateSource) {
                term = soi.product.term
                soi.termSource = 'product'
            }
        }

        if (term && soi.isActive) {
            soi.term = term

            let endDate = Utils.addMonthsAsDuration(soi.startDate, soi.term);

            if (endDateInclusive)
                endDate = Utils.addDays(endDate, -1);

            soi.endDate = Utils.formatDate(endDate);

            delete soi.dereferencedEndDateValue
            delete soi.dereferencedEndDateSource
            delete soi.dereferencedEndDate
        }
    }

    // Unable to apply dereference rules, let's default to the manual date that was set as part of upload
    // or from UI
    if (dereferenceRuleHasPriority && !soi.endDate && manualEndDate) {
        soi.endDate = manualEndDate
        soi.dereferencedEndDate = 'manualDate'
        soi.userSelectedEndDate = userSelectedEndDate
    }

    if (!soi.endDate) {
        // If recognition rule is POINT_IN_TIME than enddate is not required.
        // Otherwise mark the item inactive.
        if (ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME) {
            soi.isActive = false
        }

        // if null endDate just delete it.
        delete soi.endDate        
    }

    // If end date was explicitly selected by user and start date is after end date make end date equal start date.
    // This can happen when user has dereferenced end date for which the value is greater than user selected end date.
    if (soi.userSelectedEndDate
        && soi.endDate
        && soi.startDate
        && Period.isBefore(soi.endDate, soi.startDate)
    ) {
        soi.endDate = soi.startDate
    }

    if (rai) {
        rai.isActive = soi.isActive
        rai.deliveryStartDate = soi.startDate
        rai.deliveryEndDate = soi.endDate || soi.startDate
        rai.billingDate = soi.billingDate
    }
}

function removeRevenueArrangementsInOpenPeriod(salesOrder, organization, calendarConfig) {
    // dont worry about this if there is no close period.
    if (!organization || !salesOrder.revenueArrangement) return;

    let { currentPeriod, isRestatementMode } = organization

    if (isRestatementMode) currentPeriod = '200001'

    const revenueArrangements = []
    const revenueArrangementIds = []
    const deletedRevenueArrangementIds = []
    salesOrder.revenueArrangement.forEach(revenueArrangement => {
        const raiEffectiveActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig);
        const effectivePeriod = raiEffectiveActgPeriod.period
        // Leave all except for closed period
        if (effectivePeriod < currentPeriod || revenueArrangement.state === 'New') {
            revenueArrangements.push(revenueArrangement)
            revenueArrangementIds.push(revenueArrangement.id)
        } else {
            deletedRevenueArrangementIds.push(revenueArrangement.id)
            if (revenueArrangement.state = 'Current' && revenueArrangements.length > 0) {
                let previousRevenueArrangement = revenueArrangements[revenueArrangements.length - 1]

                previousRevenueArrangement.state = 'Current';
                delete previousRevenueArrangement.endDate
            }
        }
    })

    const currentRaId = salesOrder.currentRevenueArrangement && salesOrder.currentRevenueArrangement.id

    if (deletedRevenueArrangementIds.length > 0) {
        salesOrder.deletedRevenueArrangementIds = deletedRevenueArrangementIds;
        salesOrder.revenueArrangement = revenueArrangements;
        salesOrder.revenueArrangementIds = revenueArrangementIds;

        if (deletedRevenueArrangementIds.includes(currentRaId)) {
            delete salesOrder.currentRevenueArrangement
        }
    }
}

function getAutoCreateSSP(orgConfig) {
    const autoCreateSSPConfig = orgConfig.find(config => config.id === 'properties/auto-create-ssp');
    const autoCreateSSP = autoCreateSSPConfig && autoCreateSSPConfig.value || false

    return autoCreateSSP
}

export function populateRevenueArrangements(salesOrder, clientReferenceData, errors) {

    let { options, journalAccounts, clientAttributes, organization, orgConfig } = clientReferenceData

    // By default create the revenue plans for all sales items.
    options = Object.assign({
        createRevenuePlan: true
    }, options)

    const d = orgConfig.find(config => config.id === 'properties/calendar')
    const calendarConfig = d && d.value && d.value.config;

    const unearnedByConfig = orgConfig.find(config => config.id === 'properties/unearned_by')
    const unearnedByValue = unearnedByConfig && unearnedByConfig.value || 'customer'

    // no sales order item yet, so bbye.
    if (!salesOrder.salesOrderItem || salesOrder.salesOrderItem.length == 0) {
        return;
    }

    const orgConfigByKey = Utils.getOrgConfigByKey(orgConfig, false)

    if (!salesOrder.revenueArrangement)
        salesOrder.revenueArrangement = [];

    const revenueArrangements = salesOrder.revenueArrangement;

    salesOrder.newRevenueArrangement = revenueArrangements && revenueArrangements.find(revenueArrangement => revenueArrangement.state == 'New');
    salesOrder.currentRevenueArrangement = revenueArrangements.find(revenueArrangement => revenueArrangement.state == 'Current');

    if ((salesOrder.orderType == 'Modify' || salesOrder.orderType == 'New' || salesOrder.orderType == 'Validation Error')) {
        removeRevenueArrangementsInOpenPeriod(salesOrder, organization, calendarConfig);
        if (!salesOrder.newRevenueArrangement) {
            newRevenueArrangement(salesOrder);
        }
    }

    const _period = (organization && organization.currentPeriod) || '200101'
    const orgPeriod = Period.toActgPeriod(_period, calendarConfig)
    let currentPeriodEndDate = orgPeriod.endDate

    if (salesOrder.newRevenueArrangement) {

        if (Period.isAfter(salesOrder.orderDate, currentPeriodEndDate)) {
            // order is in the future, revenue should also be in future
            currentPeriodEndDate = salesOrder.orderDate
        }

        if (salesOrder.revenueStartDate && Period.isAfter(salesOrder.revenueStartDate, currentPeriodEndDate)) {
            // revenue start date is in the future
            currentPeriodEndDate = salesOrder.revenueStartDate
        }

        salesOrder.newRevenueArrangement.effectiveDate = currentPeriodEndDate
        salesOrder.newRevenueArrangement.postingDate = currentPeriodEndDate
    }

    // Add a rev arrangement item for all new salesOrderItems.
    salesOrder.newRevenueArrangement && salesOrder.salesOrderItem.forEach(soItem => {
        let raItem = salesOrder.newRevenueArrangement.revenueArrangementItem.find(raItem => raItem.salesOrderItemId == soItem.id)
        if (!raItem) {
            salesOrder.newRevenueArrangement.revenueArrangementItem.push(newRevenueArrangementItem(soItem, salesOrder.newRevenueArrangement.id));
        }
    });

    // No arrangements yet.
    if (salesOrder.revenueArrangement.length == 0) return;

    // Assign SalesOrderItems.
    // We cant do it in the loop below as SSP calucation requires
    // all arrangement items to have it prepopulated.
    salesOrder.revenueArrangement.forEach(
        revenueArrangement => {
            revenueArrangement.revenueArrangementItem.forEach(item => {

                // Restore snapshots used for this version.
                restoreItemSnapshots(item);

                if (!item.salesOrderItem) {
                    item.salesOrderItem = salesOrder.salesOrderItem.find(salesOrderItem => salesOrderItem.id == item.salesOrderItemId);
                } else {
                    const soi = salesOrder.salesOrderItem.find(salesOrderItem => {
                        var itemId = salesOrderItem.rootId || salesOrderItem.id;
                        var raiSoiId = item.salesOrderItem && (item.salesOrderItem.rootId || item.salesOrderItem.id);
                        if (itemId == raiSoiId) {
                            return salesOrderItem;
                        }
                    });
                    if (soi) {
                        item.salesOrderItem.attributes = item.salesOrderItem.attributes || {}
                        soi.attributes = soi.attributes || {}
                        Object.assign(item.salesOrderItem.attributes, soi.attributes)
                        if (soi.product.isMRREnabled != item.salesOrderItem.product.isMRREnabled) {
                            item.salesOrderItem.product.isMRREnabled = soi.product.isMRREnabled
                        }
                    }
                }
            })
        }
    )

    let foundSSP = true

    Utils.resetRuntime(salesOrder);

    try {
        salesOrder.revenueArrangement.forEach(
            (revenueArrangement, raIndex) => {

                let foundNonZeroSSP = false

                revenueArrangement.revenueArrangementItem.forEach(item => {
                    let { salesOrderItem } = item;

                    item.effectiveDate = revenueArrangement.effectiveDate;
                    item.endDate = revenueArrangement.endDate;

                    // Populate custom client attribute;
                    populateClientAttributes(clientAttributes, ['revenueArrangementItem'], {
                        revenueArrangementItem: item,
                        salesOrder
                    });

                    // SSP is already loaded from snapshot.
                    if (!item.ssp) {
                        selectSSP(clientReferenceData, item, salesOrder);

                        // Client has explicitly set standaone selling price of this item.
                        // So so standalonePrice gets overridden.
                        if (item.ssp && (item.salesOrderItem.standaloneSellingPrice || item.salesOrderItem.isDeleted)) {
                            let override_ssp = Utils.copy(item.ssp);
                            override_ssp.id = shortid.generate()
                            override_ssp.standalonePrice = item.salesOrderItem.isDeleted === true ? 0 : item.salesOrderItem.standaloneSellingPrice
                            item.ssp = override_ssp
                        }

                        if (!item.ssp && item.salesOrderItem.standaloneSellingPrice) {
                            const override_ssp = Utils.copy(defaultSSPForProductCode(shortid.generate()))
                            override_ssp.attributes['productCode'] = "TERMINATE";

                            if (item.salesOrderItem.product.recurring == true) {
                                override_ssp.recognitionRuleId = RecognitionRule.RATABLE
                            }

                            override_ssp.standalonePrice = item.salesOrderItem.standaloneSellingPrice
                            override_ssp.sspType = 'Revenue'

                            item.ssp = override_ssp
                        }


                        const autoCreateSSP = getAutoCreateSSP(orgConfig)

                        if (!item.ssp && autoCreateSSP == true) {

                            const { fetchBusinessKeyValues } = require('./SalesOrderVisitor')

                            let sspFieldsConfig = orgConfig.find(cfg => cfg.id === 'properties/ssp-fields')
                            let sspFields = (sspFieldsConfig && sspFieldsConfig.value) || []
                            const sspKeyBuilder = Utils.createCompositeKey(sspFields);

                            const missingSSP = fetchBusinessKeyValues(salesOrder, salesOrderItem, sspFields)

                            const override_ssp = Utils.copy(defaultSSPForProductCode(shortid.generate(), missingSSP))
                            override_ssp.id = Utils.searchValue(sspKeyBuilder(missingSSP))

                            override_ssp.standalonePrice = { "type": "AS_IS" }

                            if (item.salesOrderItem.product.code.toLowerCase().startsWith("discount")) {
                                override_ssp.recognitionRuleId = RecognitionRule.POINT_IN_TIME
                                override_ssp.standalonePrice = 0
                            } else {

                                if (item.salesOrderItem.product.recurring == true) {
                                    override_ssp.recognitionRuleId = RecognitionRule.RATABLE
                                }
                            }

                            override_ssp.sspType = 'Revenue'

                            item.ssp = override_ssp

                            // Save the auto created SSP to be saved later.
                            if (!salesOrder.autoCreatedSSP)
                                salesOrder.autoCreatedSSP = {}
                            salesOrder.autoCreatedSSP[item.ssp.id] = item.ssp
                        }
                    }

                    if (!item.ssp) {
                        errors.add(Codes.TRAN_11, { salesOrder, salesOrderItem, revenueArrangementItem: item })
                        foundSSP = false
                    }

                    const soi = getSOI(salesOrder, salesOrderItem.rootId || salesOrderItem.id)

                    if (item.ssp
                        // && (!item.deliveryStartDate || soi.startDate !== item.deliveryStartDate)
                        && (item.isActive == undefined || item.isActive == false)) {

                        const cfConfig = orgConfig.find(config => config.id == 'customFields/attributeMapping');
                        const cFields = cfConfig && cfConfig.value && Object.values(cfConfig.value);

                        const d = orgConfig.find(config => config.id == "properties/ratable_end_date_inclusive")
                        const endDateInclusive = d && d.value || false;

                        const d1 = orgConfig.find(config => config.id == "properties/dereference_rule_has_priority")
                        const dereferenceRuleHasPriority = d1 && d1.value || false;

                        populateDeliveryDates(salesOrder, soi, item.ssp, item, errors,
                            cfConfig && cFields, endDateInclusive, dereferenceRuleHasPriority)

                    }

                    soi.listPrice =
                        Utils.isNumber(soi.listPrice) ? soi.listPrice : computeTermListPrice(soi.product, soi.startDate, soi.endDate)
                    soi.extendedListPrice = soi.quantity * soi.listPrice;
                    // Populate listPrice and extended listPrice

                    populateAnnualExtendedSalePrice(soi);
                    //annual-ize sales price

                    const _soi = getSOI(salesOrder, salesOrderItem.rootId || salesOrderItem.id)
                    if (item.ssp && !_soi.isActive) {
                        const customFieldsConfig = orgConfig.find(config => config.id === 'customFields/attributeMapping');
                        errors.add(Codes.TRAN_30, {
                            salesOrder,
                            salesOrderItem: _soi,
                            customFields: customFieldsConfig && customFieldsConfig.value
                        })
                    }

                    if (item.ssp && item.ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME) {
                        const soi = getSOI(salesOrder, salesOrderItem.rootId || salesOrderItem.id)

                        if (soi.endDate && soi.startDate) {

                            let duration = Utils.getMonthDiff(soi.startDate, soi.endDate, false);
                            const d3 = orgConfig.find(cfg => cfg.id === 'properties/max-contract-years')
                            const maxContractYears = (d3 && d3.value && Number(d3.value)) || 15

                            if (duration > maxContractYears * 12) {
                                errors && errors.add(Codes.TRAN_15, { salesOrder, salesOrderItem, maxContractYears });
                            } else if (duration < 0) {
                                errors && errors.add(Codes.TRAN_18, { salesOrder, salesOrderItem });
                            }
                        }
                    }
                })

                if (foundSSP) {
                    let totalESP = 0
                    let standaloneESP = 0

                    let sspType = item => (item.ssp.standalonePrice && typeof item.ssp.standalonePrice === 'object' && item.ssp.standalonePrice.type == 'RESIDUAL') ? 'RESIDUAL' : 'NON_RESIDUAL';

                    revenueArrangement.revenueArrangementItem.sort(
                        Comparator.getComparator(
                            [sspType],
                            [Comparator.forType('string')],
                            false
                        )
                    );

                    revenueArrangement.revenueArrangementItem.forEach(item => {
                        let { salesOrderItem } = item;

                        if (Utils.isEmpty(item.estimatedSalePrice)) {
                            item.estimatedSalePrice = getStandAlonePrice(item, revenueArrangement, salesOrder);
                        }

                        if (Utils.isNumber(item.estimatedSalePrice) && item.estimatedSalePrice != 0 && (item.ssp.standalonePrice && item.ssp.standalonePrice.type !== "STANDALONE")) {
                            foundNonZeroSSP = true
                        }

                        if (Utils.isEmpty(item.totalBooking)) {
                            item.totalBooking = salesOrder.totalBooking;
                        }

                        item.actualSalePrice = salesOrderItem.actualSalePrice
                        item.extendedSalePrice = salesOrderItem.extendedSalePrice

                        item.extendedEstimatedSalePrice = item.estimatedSalePrice * salesOrderItem.quantity

                        if (item.ssp.standalonePrice && item.ssp.standalonePrice.type == "STANDALONE") {
                            // We separated STANDALONE esp thkning about the significant financing component.
                            // That should not have been handled with STANDALONE.
                            standaloneESP += item.extendedEstimatedSalePrice
                        } else {
                            totalESP += item.extendedEstimatedSalePrice;
                        }

                        if (!item.recognitionRuleId == RecognitionRule.PERCENT_OF_COMPLETE
                            && Utils.isEmpty(salesOrderItem.estimatedTotalCost)
                        ) {
                            errors.add(Codes.TRAN_11, { salesOrder, salesOrderItem, revenueArrangementItem: item })
                            foundSSP = false
                        }
                    })

                    revenueArrangement.revenueArrangementItem.forEach((item, raiIndex) => {
                        item.deliveryStartDate = item.deliveryStartDate && formatDate(item.deliveryStartDate);
                        item.deliveryEndDate = item.deliveryEndDate && formatDate(item.deliveryEndDate);

                        item.goLiveDate = item.goLiveDate && formatDate(item.goLiveDate);

                        if (!item.deliveryStartDate && item.goLiveDate) {
                            item.deliveryStartDate = item.goLiveDate;
                        } else if (item.deliveryStartDate && !item.goLiveDate) {
                            item.goLiveDate = item.deliveryStartDate;
                        }

                        if (!foundNonZeroSSP) {
                            item.productRevenue = item.extendedSalePrice
                        } else if (item.ssp.standalonePrice && item.ssp.standalonePrice.type == "STANDALONE") {
                            // We separated STANDALONE esp thinking about the significant financing component.
                            // That should not have been handled with STANDALONE.
                            item.productRevenue = item.extendedEstimatedSalePrice
                        } else {
                            let relativeSSPProportion = (item.extendedEstimatedSalePrice / totalESP) || 0
                            item.productRevenue = relativeSSPProportion * (item.totalBooking - standaloneESP)
                        }

                        item.relativeSalePricePercent = (item.productRevenue / item.totalBooking) || 0

                        // If totalSSP nets to Zero but the items are non zero
                        if (foundNonZeroSSP && totalESP == 0) {
                            item.productRevenue = item.extendedEstimatedSalePrice
                        }

                        if (!item.recognitionRule && item.ssp && item.ssp.recognitionRuleId) {
                            const ruleKey = Object.keys(RecognitionRule).find((ruleKey) => {
                                const value = RecognitionRule[ruleKey];
                                if (value == item.ssp.recognitionRuleId) {
                                    return true;
                                }
                            });

                            item.recognitionRule = { id: item.ssp.recognitionRuleId, name: ruleKey };

                            item.recognitionRuleId = item.ssp.recognitionRuleId;
                        }

                        if (options.createRevenuePlan
                            && ((revenueArrangement.state == 'New'
                                && item.recognitionRule
                                && !item.hasManualUpdates) || (
                                    !item.plan || item.plan.length == 0
                                ))) {
                            buildDefaultRevenuePlan(item, salesOrder, orgConfig)
                        }

                        if (item.plan &&
                            (item.recognitionRule.id == RecognitionRule.PROPORTIONAL_PERFORMANCE
                                || item.recognitionRule.id == RecognitionRule.PERCENT_OF_COMPLETE)
                        ) {
                            // Taking backup of original Plan
                            if (!item.originalPlan) {
                                item.originalPlan = Utils.copy(item.plan)
                            }

                            const enableBetaFeatures = (
                                orgConfigByKey['properties/enableBetaFeatures']
                                && orgConfigByKey['properties/enableBetaFeatures'].value
                            ) || false

                            updatePSPlan(item, salesOrder, errors, enableBetaFeatures);
                        }

                        if (item.plan && unearnedByValue != 'customer') {
                            addBillingPeriodsToPlan(item.plan, salesOrder, calendarConfig)
                        }

                        if (journalAccounts && journalAccounts.length > 0) {

                            // Revenue Account
                            if (item.revenueAccountId) {
                                item.revenueAccount = journalAccounts.find(journalAccount => journalAccount.id == item.revenueAccountId)
                            }

                            // Deferred Revenue Account
                            if (item.liabilityAccountId) {
                                item.liabilityAccount = journalAccounts.find(journalAccount => journalAccount.id == item.liabilityAccountId)
                            }

                            if (item.contraRevenueAccountId) {
                                item.contraRevenueAccount = journalAccounts.find(journalAccount => journalAccount.id == item.contraRevenueAccountId)
                            }

                            // Deferred Commission Account
                            if (item.deferredCommissionAccountId) {
                                item.deferredCommissionAccount = journalAccounts.find(journalAccount => journalAccount.id == item.deferredCommissionAccountId)
                            }

                            // Amortization Commission Account
                            if (item.commissionAmortizationAccountId) {
                                item.commissionAmortizationAccount = journalAccounts.find(journalAccount => journalAccount.id == item.commissionAmortizationAccountId)
                            }

                            // Contra Commission Account
                            if (item.contraCommissionExpenseAccountId) {
                                item.contraCommissionExpenseAccount = journalAccounts.find(journalAccount => journalAccount.id == item.contraCommissionExpenseAccountId)
                            }

                            // Deferred Commission; accumulated am
                            if (item.commissionAccumulatedAmortizationAccountId) {
                                item.commissionAccumulatedAmortizationAccount = journalAccounts.find(journalAccount => journalAccount.id == item.commissionAccumulatedAmortizationAccountId)
                            }
                        }
                    })
                }
            }
        )

        //populate the annual booking amount for the order.
        salesOrder.totalAnnualBooking =
            salesOrder.salesOrderItem.reduce((booking, salesOrderItem) => booking += salesOrderItem.annualExtendedSalePrice, 0.0);

        // Populate custom client attribute;
        populateClientAttributes(clientAttributes, ['salesOrder'], { salesOrder });

        processExpense(clientReferenceData, salesOrder, errors, orgConfig, options)

    } finally {
        Utils.disposeRuntime(salesOrder);
    }

}

export function processExpense(clientReferenceData, salesOrder, errors, orgConfig, options) {

    if (!salesOrder.expenseItems || (salesOrder.expenseItems && salesOrder.expenseItems.length == 0)) {
        // we don't have expense to process
        return
    }

    const { buildExpenseRuleDistinctiveKey } = require('./SalesOrderVisitor')

    let sspFieldsConfig = orgConfig.find(cfg => cfg.id === 'properties/ssp-fields')
    let sspFields = (sspFieldsConfig && sspFieldsConfig.value) || []

    const d = orgConfig.find(config => config.id === 'properties/calendar')
    const calendarConfig = d && d.value && d.value.config;

    const getExpenseItemUniqKey = (item) => {
        return `${item.salesOrderItemRootId}_${item.referenceNumber}_${item.productCode}_${item.expenseCode}_${item.class}_${item.department}_${item.location}`
    }

    const buildExpenseRuleId = (item) => {
        let id = `${item.expenseCode}`

        if (item.productCode) {
            id += `_${item.productCode}`
        }

        return id
    }

    const newDirectExpenseRules = []
    const newInDirectExpenseRules = []

    const getDirectExpenseRule = (raItem, raiExpenseItem) => {
        const salesOrderItemSSP = raItem.ssp
        const attributes = salesOrderItemSSP.attributes
        const ruleId = buildExpenseRuleId(raiExpenseItem)
        const newRule = defaultDirectExpenseRule(ruleId, attributes, raiExpenseItem.expenseCode)

        if (newDirectExpenseRules.length) {
            const distinctiveKey = buildExpenseRuleDistinctiveKey(newRule, sspFields)
            newDirectExpenseRules.forEach(rule => rule.distinctiveKey = buildExpenseRuleDistinctiveKey(rule, sspFields))

            const groupedByDistinctiveKey = _.groupBy(newDirectExpenseRules, r => r.distinctiveKey)

            if (Object.keys(groupedByDistinctiveKey).includes(distinctiveKey)) {
                // we already created the ssp, let's return this ssp now
                return groupedByDistinctiveKey[distinctiveKey][0]
            }
        }

        // new ssp created
        newDirectExpenseRules.push(newRule)
        return newRule
    }

    const getInDirectExpenseRule = (raiExpenseItem) => {
        const ruleId = buildExpenseRuleId(raiExpenseItem)
        const newRule = defaultInDirectExpenseRule(ruleId, raiExpenseItem)

        if (newInDirectExpenseRules.length) {
            const distinctiveKey = buildExpenseRuleDistinctiveKey(newRule, sspFields)
            newInDirectExpenseRules.forEach(rule => rule.distinctiveKey = buildExpenseRuleDistinctiveKey(rule, sspFields))

            const groupedByDistinctiveKey = _.groupBy(newInDirectExpenseRules, r => r.distinctiveKey)

            if (Object.keys(groupedByDistinctiveKey).includes(distinctiveKey)) {
                // we already created the ssp, let's return this ssp now
                return groupedByDistinctiveKey[distinctiveKey][0]
            }
        }

        // new ssp created
        newInDirectExpenseRules.push(newRule)
        return newRule
    }

    const checkIfExpenseIsAlreadyAssignedToProductBefore = (expenseItem, revenueArrangement) => {
        const expenseKey = `${expenseItem.expenseCode}_${expenseItem.referenceNumber}_${expenseItem.productCode}_${expenseItem.class}_${expenseItem.department}_${expenseItem.location}`

        let _expenseItem
        for (const rai of revenueArrangement.revenueArrangementItem) {
            if (rai.expenseItems && rai.expenseItems.length) {
                _expenseItem = rai.expenseItems.find(expItem => {
                    const expItemKey = `${expItem.expenseCode}_${expItem.referenceNumber}_${expItem.productCode}_${expItem.class}_${expItem.department}_${expItem.location}`

                    if (expItemKey == expenseKey) {
                        return expItem
                    }
                })

                if (_expenseItem) {
                    // we have found the matching item
                    break;
                }
            }
        }

        if (_expenseItem && _expenseItem.expenseAmount == expenseItem.expenseAmount) {
            return true
        }

        return false
    }

    // we have expense items
    for (const revenueArrangement of salesOrder.revenueArrangement) {

        const contractTermConfig = orgConfig.find(config => config.id == "properties/expense_for_contract_term")
        const contractTermValue = contractTermConfig && contractTermConfig.value || false;

        let raiItemsGroupedByProductCode

        if (contractTermValue) {
            raiItemsGroupedByProductCode = Utils.groupByOnArray(revenueArrangement.revenueArrangementItem, _ => _.salesOrderItem.product.code)
        }

        for (const raItem of revenueArrangement.revenueArrangementItem) {
            const { salesOrderItem } = raItem
            for (const expenseItem of revenueArrangement.expenseItems) {
                // STEP 0: Get expense item from raItem
                let raiExpenseItem = undefined
                let newRaiExpenseItem = false
                if (raItem.expenseItems.length) {
                    // this would only be returned on day2
                    // this should never return on day1
                    const _raiExpenseItem = raItem.expenseItems.find(expItem => {
                        if (expItem.expenseCode == expenseItem.expenseCode
                            && expItem.productCode == expenseItem.productCode
                            && expItem.class == expenseItem.class
                            && expItem.department == expenseItem.department
                            && expItem.location == expenseItem.location
                            && expItem.referenceNumber == expenseItem.referenceNumber) {
                            return expItem
                        }
                    })

                    raiExpenseItem = Utils.cloneDeep(_raiExpenseItem)
                } else {
                    raItem.expenseItems = []
                }

                if (!raiExpenseItem) {
                    if (expenseItem.salesOrderItemId) {
                        // this expense is sent for particular sales order item
                        // so assign it to that item and that item only
                        const salesOrderItemId = (salesOrderItem.rootId || salesOrderItem.id)
                        if (expenseItem.salesOrderItemId != salesOrderItemId) {
                            continue;
                        }
                    } else {
                        // expense is not sent for any sales order item.
                    }
                    raiExpenseItem = Utils.cloneDeep(expenseItem)
                    raiExpenseItem.id = `expense-${shortid.generate()}`
                    raiExpenseItem.salesOrderItemId = salesOrderItem.id
                    raiExpenseItem.salesOrderItemRootId = salesOrderItem.rootId
                    newRaiExpenseItem = true
                }

                // STEP 1: select expense rule
                if (!raiExpenseItem.expenseRule) {
                    let expenseRule = selectExpenseRule(clientReferenceData, expenseItem, expenseItem.expenseType == 'Direct' ? 'Direct' : 'Indirect');

                    // no ssp check if auto is enabled
                    // yes, create default with expense tracks revenue = true and reAllocate = true
                    const autoCreateSSP = getAutoCreateSSP(orgConfig)
                    if (!expenseRule && autoCreateSSP == true) {
                        if (raiExpenseItem.expenseType == 'Direct') {
                            expenseRule = getDirectExpenseRule(raItem, raiExpenseItem)
                        } else {
                            // Indirect expense
                            expenseRule = getInDirectExpenseRule(raiExpenseItem)
                        }

                        // Save the auto created SSP to be saved later.
                        if (!salesOrder.autoCreatedSSP)
                            salesOrder.autoCreatedSSP = {}
                        if (!salesOrder.autoCreatedSSP[expenseRule.id]) {
                            salesOrder.autoCreatedSSP[expenseRule.id] = expenseRule
                        }
                    }

                    if (expenseRule) {
                        raiExpenseItem.expenseRule = Utils.cloneDeep(expenseRule)
                        // STEP 1.a: Expense Tracks Revenue is enabled
                        //      - copy expense amortization method from raiExpenseItem.ssp
                        //      - copy delivery stuff from raiExpenseItem.ssp
                        // STEP 1.b: Expense Tracks Revenue is disabled
                        //       - Use Amortization Method from raiExpenseItem.expenseRule
                        //       - Use delivery stuff from raiExpenseItem.expenseRule
                        if (expenseRule.expenseTracksRevenue == true) {
                            if (raItem.ssp.recognitionRuleId == RecognitionRule.POINT_IN_TIME) {
                                raiExpenseItem.expenseRule.expenseAmortizationMethod = ExpenseRecognitionRule.AS_INCURRED
                            } else {
                                raiExpenseItem.expenseRule.expenseAmortizationMethod = ExpenseRecognitionRule.OVER_TIME
                            }
                        }
                    }
                }

                if (!raiExpenseItem.expenseRule) {
                    // TODO: ERROR condition, we can't find expense rule.
                    errors.add(Codes.EXPENSE_7, { expenseItem: raiExpenseItem })
                    console.error(`Expense rule not found for ${expenseItem}, ${raItem}, ${raiExpenseItem}`)
                    continue
                }

                // STEP 2.a: reAllocate expense is enabled, do reAllocation, otherwise no reallocation assign the full amount the matching item.
                if (raiExpenseItem.expenseRule.reAllocateExpense) {
                    // reAllocation is enabled
                    const allocatedExpense = raItem.relativeSalePricePercent * raiExpenseItem.expenseAmount
                    raiExpenseItem.allocatedExpense = allocatedExpense

                    if (expenseItem.salesOrderItemId) {
                        // unfortunately the way we used to calculate allocatedCommission was
                        // to sum all commission expenses for all items and then apply proportion
                        let totalExpense = 0
                        revenueArrangement.expenseItems.forEach(ex => {
                            if (!ex.isDeleted) {
                                // this expense was removed so no need to add this.
                                totalExpense += ex.expenseAmount
                            }
                        })

                        // let's check if we already have assigned something to this sales order item
                        // multiple expenses coming for same item scenario
                        let alreadyAllocatedExpenseForSalesOrderItem = 0
                        if (raItem.expenseItems.length) {
                            // we have found expense
                            // let's subtract the amount we have already allocated to this item
                            raItem.expenseItems.forEach(ex => {
                                const salesOrderItemId = salesOrderItem.rootId || salesOrderItem.id
                                const expSalesOrderItemId = ex.salesOrderItemRootId || ex.salesOrderItemId
                                // make sure it's not the same we are iterating right now.
                                if ((salesOrderItemId == expSalesOrderItemId
                                    && ex.referenceNumber != raItem.referenceNumber
                                    && !ex.isDeleted)) {
                                    alreadyAllocatedExpenseForSalesOrderItem += ex.allocatedExpense
                                }
                            })
                        }
                        raiExpenseItem.allocatedExpense = raItem.relativeSalePricePercent * totalExpense

                        if (alreadyAllocatedExpenseForSalesOrderItem) {
                            raiExpenseItem.allocatedExpense -= alreadyAllocatedExpenseForSalesOrderItem
                        }

                    }
                } else {
                    // reAllocation is disabled
                    if (expenseItem.expenseType == 'Direct' &&
                        expenseItem.productCode &&
                        (expenseItem.productCode != salesOrderItem.product.code)) {

                        if (checkIfExpenseIsAlreadyAssignedToProductBefore(raiExpenseItem, revenueArrangement)) {
                            // Seems like we have multiple items in the current rai
                            // since we have already assigned expense to the product, no to raise this error
                            // for this rai.salesOrderItem
                            continue
                        }

                        errors.add(Codes.EXPENSE_6, { expenseItem: raiExpenseItem })
                        continue
                    } else {
                        let isExpenseAlreadyAssigned = checkIfExpenseIsAlreadyAssignedToProductBefore(raiExpenseItem, revenueArrangement)

                        if (!isExpenseAlreadyAssigned) {
                            // only assigning if we haven't previously assigned to the same product
                            // or the expense amount has changed
                            const allocatedExpense = raiExpenseItem.expenseAmount
                            raiExpenseItem.allocatedExpense = allocatedExpense
                        }
                    }
                }

                // STEP 2.b: Discount Item don't do any processing just return
                if (salesOrderItem.product.code.toLowerCase().startsWith("discount") && (
                    raItem.relativeSalePricePercent == undefined
                    || (raItem.relativeSalePricePercent != undefined && raItem.relativeSalePricePercent == 0)
                )) {
                    raiExpenseItem.allocatedExpense = 0
                }

                // STEP 2.c If this sales order item has been deleted, change the allocated expense to 0
                if (salesOrderItem.isDeleted) {
                    raiExpenseItem.allocatedExpense = 0
                }

                // STEP 2.d expense item has been deleted, change the allocated expense to 0
                if (raiExpenseItem.isDeleted) {
                    raiExpenseItem.allocatedExpense = 0
                }

                // STEP 3: Populate Delivery Dates
                if (raItem.isActive) {
                    // only populate when this raItem is active

                    const d = orgConfig.find(config => config.id == "properties/ratable_end_date_inclusive")
                    const endDateInclusive = d && d.value || false;

                    populateExpenseDeliveryDates(raiExpenseItem, raiExpenseItem.expenseRule, raItem, errors, endDateInclusive, contractTermValue, raiItemsGroupedByProductCode)

                    if (raiExpenseItem.deliveryStartDate == undefined
                        && raiExpenseItem.expenseType == 'Indirect'
                        && raiExpenseItem.expenseRule.expenseAmortizationMethod == ExpenseRecognitionRule.AS_INCURRED) {
                        raiExpenseItem.deliveryStartDate = raiExpenseItem.expenseDate
                    }
                }

                if (!raiExpenseItem.expenseRecognitionRule && raiExpenseItem.expenseRule && raiExpenseItem.expenseRule.expenseAmortizationMethod) {
                    const ruleKey = Object.keys(ExpenseRecognitionRule).find((ruleKey) => {
                        const value = ExpenseRecognitionRule[ruleKey];
                        if (value == raiExpenseItem.expenseRule.expenseAmortizationMethod) {
                            return true;
                        }
                    });

                    raiExpenseItem.expenseRecognitionRule = { id: raiExpenseItem.expenseRule.expenseAmortizationMethod, name: ruleKey };
                    raiExpenseItem.expenseRecognitionRuleId = raiExpenseItem.expenseRule.expenseAmortizationMethod;
                }

                if (options.createRevenuePlan
                    && ((revenueArrangement.state == 'New'
                        && raiExpenseItem.expenseRecognitionRule) || (
                            !raiExpenseItem.plan || raiExpenseItem.plan.length == 0
                        ))) {

                    try {
                        buildDefaultExpensePlan(raiExpenseItem, raItem, salesOrder, orgConfig)
                    } catch (e) {
                        console.error(`raiExpenseItem ${JSON.stringify(raiExpenseItem, null, 4)}, salesOrder: ${salesOrder.id}`)
                        throw e
                    }

                }

                // STEP 5: revenuearrAngementItem.expenseItems.push()
                if (newRaiExpenseItem) {
                    raItem.expenseItems.push(raiExpenseItem)
                } else {
                    // we have previouslly added this item, no need to do anything :)
                }
            }
        }
    }

    // Here we update any expense amortization plan for with the parent arrangement item has Proportional Performance Rule.
    // We ony support proportional performance for expense where expense tracks revenue is true.
    const updateProportionalPerformancePlans = () => {
        for (const revenueArrangement of salesOrder.revenueArrangement) {
            for (const raItem of revenueArrangement.revenueArrangementItem) {
                if (!raItem.expenseItems) continue;

                for (const raiExpenseItem of raItem.expenseItems) {
                    if (raItem.plan &&
                        raItem.recognitionRule.id == RecognitionRule.PROPORTIONAL_PERFORMANCE &&
                        raiExpenseItem.expenseRule.expenseTracksRevenue == true
                    ) {
                        raiExpenseItem.plan = Utils.cloneDeep(raItem.plan)

                        raiExpenseItem.plan.forEach(
                            planItem => planItem.actgPeriod = Period.toActgPeriod(planItem.period, calendarConfig)
                        )

                        updateRatablePlan(raiExpenseItem.plan, raiExpenseItem.allocatedExpense)
                    }
                }
            }
        }
    }

    const updateSalesOrderExpenseItemId = () => {
        for (const revenueArrangement of salesOrder.revenueArrangement) {
            for (const raItem of revenueArrangement.revenueArrangementItem) {
                if (raItem.expenseItems && raItem.expenseItems.length) {
                    for (const raiExpenseItem of raItem.expenseItems) {
                        raiExpenseItem.salesOrderExpenseItemId = getExpenseItemUniqKey(raiExpenseItem)
                    }
                }
            }
        }
    }

    updateProportionalPerformancePlans()

    updateSalesOrderExpenseItemId()

}

const snapshotKeys = ['salesOrderItem', 'ssp', 'attributes', 'estimatedSalePrice', 'totalBooking']

// Restore saved snapshot for revenue arrangement item, if available.
const restoreItemSnapshots = (revenueArrangementItem) => {
    let _snapshotKeys = ['salesOrderItem', 'attributes', 'estimatedSalePrice', 'totalBooking']
    if (revenueArrangementItem.snapshots) {
        _snapshotKeys = snapshotKeys
    }

    // Clear all prior snapshots.
    _snapshotKeys.forEach(key => {
        delete revenueArrangementItem[key]
    });

    revenueArrangementItem.snapshots && Object.keys(revenueArrangementItem.snapshots).forEach(
        key => {
            if (snapshotKeys.includes(key)) {
                revenueArrangementItem[key] = revenueArrangementItem.snapshots[key]
            }
        }
    )
}

export function _nextVersion(salesOrderItemId) {
    var myRegexp = /_v([\d]+)$/;
    var match = myRegexp.exec(salesOrderItemId);
    var priorVersion = (match && match[1]) || 0;
    return Number(priorVersion) + 1;
}

export function getSalesOrderItemRootId(salesOrderItemId) {
    let _myRegexp = /_v([\d]+)$/;
    return salesOrderItemId.replace(_myRegexp, "")
}

export const createSnapshotsForCurrentArrangement = (salesOrder) => {

    let revenueArrangement = salesOrder.currentRevenueArrangement;

    let count = _nextVersion(
        (salesOrder.salesOrderItem
            && salesOrder.salesOrderItem.length > 0
            && salesOrder.salesOrderItem[0].id
        ) || ""
    )

    revenueArrangement.revenueArrangementItem.forEach(item => {
        item.snapshots = {}

        snapshotKeys.forEach(key => item.snapshots[key] = Utils.copy(item[key]));

        // Change id for salesOrderItem, so when joing during reporting we dont get duplicate results.
        let { salesOrderItem } = item;
        if (!salesOrderItem.rootId) {
            // Save to root id for future sales order items.
            salesOrderItem.rootId = salesOrderItem.id
        }
        salesOrderItem.id = `${salesOrderItem.rootId}_v${count}`
        salesOrderItem.salesOrderPS && salesOrderItem.salesOrderPS.forEach(sops => sops.salesOrderItemId = salesOrderItem.id)
    })

    // Remove all un-necessary objects from revenueArrangement
    doRACleanup(revenueArrangement)

    // Change id for salesOrderItem, so when joing during reporting we dont get duplicate results.
    salesOrder.salesOrderItem && salesOrder.salesOrderItem.forEach(salesOrderItem => {
        if (!salesOrderItem.rootId) {
            // Save to root id for future sales order items.
            salesOrderItem.rootId = salesOrderItem.id
        }
        salesOrderItem.id = `${salesOrderItem.rootId}_v${count}`
        salesOrderItem.salesOrderPS && salesOrderItem.salesOrderPS.forEach(sops => sops.salesOrderItemId = salesOrderItem.id)
    });

}

export const newRevenueArrangement = (salesOrder) => {
    const orderDate = Utils.formatDate(salesOrder.orderDate);

    const revenueStartDate = salesOrder.revenueStartDate != undefined
        ? Utils.formatDate(salesOrder.revenueStartDate)
        : orderDate
    // setting revenueStartDate to salesOrder.revenueStartDate otherwaise defaulting it to orderDate

    let revenueArrangement = {}
    revenueArrangement.effectiveDate = revenueStartDate
    revenueArrangement.isRevenueDelayed = salesOrder.isRevenueDelayed
    revenueArrangement.salesOrderId = salesOrder.id
    revenueArrangement.id = shortid.generate();
    revenueArrangement.new = true;
    revenueArrangement.revenueArrangementItem = salesOrder.salesOrderItem.map((item, index) => {
        return newRevenueArrangementItem(item, revenueArrangement.id);
    })
    revenueArrangement.state = 'New';

    if (salesOrder.orderType == 'Modify') {
        revenueArrangement.modificationType = ModificationType.RETROSPECTIVE
    }

    if (salesOrder.expenseItems && salesOrder.expenseItems.length) {
        revenueArrangement.expenseItems = Utils.cloneDeep(salesOrder.expenseItems)
    } else {
        revenueArrangement.expenseItems = []
    }

    salesOrder.revenueArrangement.push(revenueArrangement);
    if (!salesOrder.revenueArrangementIds) {
        salesOrder.revenueArrangementIds = []
    }
    salesOrder.revenueArrangementIds.push(revenueArrangement.id)
    revenueArrangement.jobId = salesOrder.jobId
    salesOrder.newRevenueArrangement = revenueArrangement;
}

export const newRevenueArrangementItem = (salesOrderItem, revenueArrangementId) => {

    return {
        id: shortid.generate(),
        salesOrderItemId: salesOrderItem.id,
        salesOrderItem: salesOrderItem,
        expenseItems: [],
        revenueArrangementId: revenueArrangementId,
        liabilityAccountId: salesOrderItem.deferredRevenueAccountNumber,
        revenueAccountId: salesOrderItem.revenueAccountNumber,
        contraRevenueAccountId: salesOrderItem.contraRevenueAccountNumber,
        deferredCommissionAccountId: salesOrderItem.deferredCommissionAccountNumber,
        commissionAmortizationAccountId: salesOrderItem.commissionAccountNumber,
        contraCommissionExpenseAccountId: salesOrderItem.contraCommissionExpenseAccountNumber,
        commissionAccumulatedAmortizationAccountId: salesOrderItem.commissionAccumulatedAmortizationAccountNumber
    }
}

export function buildDefaultExpensePlan(expenseItem, raItem, salesOrder, orgConfig) {

    const d = orgConfig.find(config => config.id === 'properties/calendar')
    const calendarConfig = d && d.value && d.value.config;

    switch (expenseItem.expenseRecognitionRule.id) {
        case ExpenseRecognitionRule.AS_INCURRED:
            expenseItem.plan = PointInTimePlan.buildPlan(
                expenseItem.deliveryStartDate,
                expenseItem.deliveryEndDate,
                expenseItem.allocatedExpense,
                expenseItem.expenseRule,
                calendarConfig)
            break;
        default:
            expenseItem.plan = buildPlan({
                deliveryStartDate: expenseItem.deliveryStartDate,
                deliveryEndDate: expenseItem.deliveryEndDate
            }, salesOrder, orgConfig, expenseItem.allocatedExpense, calendarConfig);
    }

    prefillZeroAtStart(expenseItem.plan, {
        revenueArrangement: [{
            effectiveDate: expenseItem.expenseDate
        }]
    }, calendarConfig)
}

/* -------
   Default Revenue Plan
   -------  */
export function buildDefaultRevenuePlan(revenueArrangementItem, salesOrder, orgConfig) {
    if (!revenueArrangementItem || !revenueArrangementItem.recognitionRule)
        throw new Error("Missing revenue arrangement item or rule");

    const d = orgConfig.find(config => config.id === 'properties/calendar')
    const calendarConfig = d && d.value && d.value.config;

    let productRevenue = revenueArrangementItem.productRevenue

    if (salesOrder.revenueArrangement) {
        const currentRevenueArrangement = salesOrder.revenueArrangement.find(ra => ra.id == revenueArrangementItem.revenueArrangementId)

        if (currentRevenueArrangement.isRevenueDelayed) {
            productRevenue = 0
        }
    }

    switch (revenueArrangementItem.recognitionRule.id) {
        case RecognitionRule.POINT_IN_TIME:
            revenueArrangementItem.plan = PointInTimePlan.buildPlan(
                revenueArrangementItem.deliveryStartDate,
                revenueArrangementItem.deliveryEndDate,
                productRevenue,
                revenueArrangementItem.ssp,
                calendarConfig)
            break;
        case RecognitionRule.PROPORTIONAL_PERFORMANCE:
        case RecognitionRule.PERCENT_OF_COMPLETE:
            const plan = buildPlan(revenueArrangementItem, salesOrder, orgConfig, productRevenue, calendarConfig);
            plan.forEach(p => {
                p.percentRecognized = 0
                p.planAmount = 0

                delete p.cummulativePercentRecognized;
                delete p.cummulativeTotal;
            })

            if (plan.length > 0) {
                plan[plan.length - 1].percentRecognized = 1.0
                plan[plan.length - 1].cummulativePercentRecognized = 1.0
                plan[plan.length - 1].cummulativeTotal = productRevenue
                plan[plan.length - 1].planAmount = productRevenue
            }
            revenueArrangementItem.plan = plan
            break;
        default:
            revenueArrangementItem.plan = buildPlan(revenueArrangementItem, salesOrder, orgConfig, productRevenue, calendarConfig);
    }

    prefillZeroAtStart(revenueArrangementItem.plan, salesOrder, calendarConfig)
}

function buildPlan(revenueArrangementItem, salesOrder, orgConfig, productRevenue, calendarConfig) {
    let startDate = revenueArrangementItem.deliveryStartDate;
    let endDate = revenueArrangementItem.deliveryEndDate;
    let originalBooking = productRevenue;
    // delayed revenue is true our plan is 0 (in modification this will be adjustment)

    const d1 = orgConfig.find(config => config.id === "properties/ratable_plan_type");
    const planType = d1 && d1.value || "default";

    const d2 = orgConfig.find(config => config.id === "properties/ratable_end_date_inclusive")
    const endDateInclusive = d2 && d2.value || false;

    const d3 = orgConfig.find(cfg => cfg.id === 'properties/max-contract-years')
    const maxContractYears = (d3 && d3.value && Number(d3.value)) || 20

    if (calendarConfig && calendarConfig.type === 'retail') {
        if (planType !== 'daily')
            throw new Error("Retail calendar can only be used with a daily plan!");
    }

    let plan;
    if (planType === 'monthly_endmonth_exclusive') {
        plan = FullMonthRatablePlan.buildPlan(startDate,
            endDate, originalBooking, calendarConfig)
    } else if (planType === 'fixed') {

        const d3 = orgConfig.find(config => config.id === "properties/ratable_start_exact_days")
        const exactDays = d3 && d3.value || false;

        if (exactDays) {
            let { term } = revenueArrangementItem.salesOrderItem;
            plan = Modified30_360Plan.buildPlan(startDate, endDate, originalBooking, endDateInclusive, calendarConfig, term)
        }
        else {
            plan = FixedMonthRatablePlan.buildPlan(startDate,
                endDate, originalBooking, endDateInclusive, exactDays,
                calendarConfig)
        }
    } else if (planType === 'daily') {
        plan = DailyRatablePlan.buildPlan(startDate,
            endDate, originalBooking, endDateInclusive,
            calendarConfig)
    }
    else {
        plan = ApportionedRatablePlan.buildPlan(startDate, endDate,
            originalBooking, endDateInclusive)
    }

    plan = (plan.length > 12 * maxContractYears) ? plan.slice(0, 12 * maxContractYears) : plan;
    //do not support more than maxContractYears years

    return plan;
}


export function prefillZeroAtStart(plan, salesOrder, calendarConfig) {

    if (plan.length == 0) return

    const firstPlanActgPeriod =
        plan[0].actgPeriod;

    const arrangementEffectivePeriod =
        Period.toActgPeriod(salesOrder.revenueArrangement[0].effectiveDate, calendarConfig);

    if (arrangementEffectivePeriod.period >= firstPlanActgPeriod.period)
        return;

    const periods =
        Period.periodsBetween(arrangementEffectivePeriod.period, firstPlanActgPeriod.period, true);

    if (periods.length == 0) return;

    let toAdd = []
    let actgPeriod = firstPlanActgPeriod;
    for (let period of periods) {
        actgPeriod = actgPeriod.get(period);

        toAdd.push({
            id: shortid.generate(),
            actgPeriod,
            postingDate: actgPeriod.postingDate,
            effectiveDate: actgPeriod.effectiveDate,
            period,
            postingPeriod: period,
            planAmount: 0,
            percentRecognized: 0,
            cummulativePercentRecognized: 0,
            cummulativeTotal: 0
        });
    }

    plan.unshift(...toAdd);
}

const getMaxBillingDate = (salesOrder) => {
    let maxBillingDate
    // pick max billing date
    if (salesOrder.billingSchedule && salesOrder.billingSchedule.billingScheduleItem) {
        for (const bsi of salesOrder.billingSchedule.billingScheduleItem) {
            const schedule = bsi.schedule
            if (Array.isArray(schedule) && schedule.length) {
                const sortedByBillingDate = _.sortBy(schedule, s => s.billingDate)
                const lastBilling = sortedByBillingDate[sortedByBillingDate.length - 1]
                const lastBillingDate = lastBilling.billingDate

                if (!maxBillingDate) {
                    maxBillingDate = lastBillingDate
                } else if (lastBillingDate > maxBillingDate) {
                    maxBillingDate = lastBillingDate
                }
            }
        }
    }

    return maxBillingDate
}

/**
 * If we have billing that is coming after the effective date of the
 * revenue arrangement then we want to add those periods in the plan
 * with 0 so that when transactions are generated they don't have any missing
 * rows in it
 * @param {*} plan 
 * @param {*} salesOrder 
 * @param {*} calendarConfig 
 * @returns 
 */

export function addBillingPeriodsToPlan(plan, salesOrder, calendarConfig) {

    if (plan.length == 0) return

    const maxBillingDate = getMaxBillingDate(salesOrder)

    if (!maxBillingDate) return

    const lastPlanActgPeriod = plan[plan.length - 1].actgPeriod;

    const maxBillingEffectivePeriod =
        Period.toActgPeriod(maxBillingDate, calendarConfig);

    if (maxBillingEffectivePeriod.period <= lastPlanActgPeriod.period)
        return;

    const periods =
        Period.periodsBetween(lastPlanActgPeriod.period, maxBillingEffectivePeriod.period, false, true);

    if (periods.length == 0) return;

    let actgPeriod = lastPlanActgPeriod;
    for (let period of periods) {
        actgPeriod = actgPeriod.get(period);

        plan.push({
            id: shortid.generate(),
            actgPeriod,
            postingDate: actgPeriod.postingDate,
            effectiveDate: actgPeriod.effectiveDate,
            period,
            postingPeriod: period,
            planAmount: 0,
            percentRecognized: 0,
            cummulativePercentRecognized: 0,
            cummulativeTotal: 0
        });
    }
}

export function updateQuantityPlan(revenueArrangementItem, plan) {

    let availableQuantity = revenueArrangementItem.salesOrderItem.quantity;
    let originalBooking = revenueArrangementItem.productRevenue;
    let totalBooking = originalBooking
    let unitCost = totalBooking / availableQuantity;

    var last = plan[plan.length - 1]

    plan.forEach(element => {

        if (element.quantity > availableQuantity) {
            element.quantity = availableQuantity
        }

        if (element == last && element.quantity < availableQuantity) {
            element.quantity = availableQuantity
        }

        element.planAmount = unitCost * element.quantity
        element.percentRecognized = element.planAmount / originalBooking

        totalBooking -= element.planAmount
        element.cummulativeTotal = originalBooking - totalBooking;
        element.cummulativePercentRecognized = element.cummulativeTotal / originalBooking;

        availableQuantity -= element.quantity;
    });

}

export function doRACleanup(ra) {

    const cleanupSOI = (soi) => {
        delete soi.revenueAccountNumber
        delete soi.deferredRevenueAccountNumber
        delete soi.contraRevenueAccountNumber
        delete soi.commissionAccountNumber
        delete soi.deferredCommissionAccountNumber
        delete soi.commissionAccumulatedAmortizationAccountNumber
        delete soi.contraCommissionExpenseAccountNumber

        delete soi.product.jobId
        delete soi.product.lastModifiedBy
        delete soi.product.lastModifiedDate
        delete soi.product.rowNum
    }

    const cleanupExpenseItem = (expItem, removePlan = false) => {
        delete expItem.rowNum

        if (removePlan) {
            delete expItem.plan
        }

        if (expItem.expenseRule) {
            delete expItem.expenseRule.xlRow
            delete expItem.expenseRule.lastModifiedBy
            delete expItem.expenseRule.lastModifiedDate
        }

        if (expItem.salesPerson) {
            delete expItem.salesPerson.new
            delete expItem.salesPerson.lastModifiedBy
        }
    }

    for (const rai of ra.revenueArrangementItem) {

        // 1 cleanup rai level keys
        delete rai.liabilityAccountId
        delete rai.revenueAccountId
        delete rai.contraRevenueAccountId
        delete rai.deferredCommissionAccountId
        delete rai.commissionAmortizationAccountId
        delete rai.contraCommissionExpenseAccountId
        delete rai.commissionAccumulatedAmortizationAccountId

        if (rai.ssp) {
            delete rai.ssp.xlRow
            delete rai.ssp.lastModifiedBy
            delete rai.ssp.lastModifiedDate
        }

        // 2. cleanup sales order item level keys
        if (rai.salesOrderItem) {
            cleanupSOI(rai.salesOrderItem)
        }

        // 2. cleanup expense item level keys
        if (rai.expenseItems && rai.expenseItems.length) {
            for (const expItem of rai.expenseItems) {
                cleanupExpenseItem(expItem)
            }
        }

        // 3. cleanup snapshot level keys
        if (rai.snapshots) {
            if (rai.snapshots.ssp) {
                delete rai.snapshots.ssp.xlRow
                delete rai.snapshots.ssp.lastModifiedBy
                delete rai.snapshots.ssp.lastModifiedDate
            }

            if (rai.snapshots.salesOrderItem) {
                cleanupSOI(rai.snapshots.salesOrderItem)
            }

            if (rai.snapshots.expenseItems && rai.snapshots.expenseItems.length) {
                delete rai.snapshots.expenseItems
            }
        }
    }
}